import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, forkJoin, switchMap, tap } from 'rxjs';
import { PatientService } from './patient.service';
import { SocketService } from './socket.service';

@Injectable({
  providedIn: 'root'
})
export class PatientCommunicationService {

  private apiUrl = `api/patient-communication`;
  private patientCommunicationSubject = new BehaviorSubject<any[] | null>(null);
  private patientEmailsSubject = new BehaviorSubject<any[] | null>(null);
  private patientMediaSubject = new BehaviorSubject<any[] | null>(null);

  patientCommunication$ = this.patientCommunicationSubject.asObservable();
  patientEmails$ = this.patientEmailsSubject.asObservable();
  patientMedia$ = this.patientMediaSubject.asObservable();
  patientId?: string;

  constructor(
    private http: HttpClient,
    private patientService: PatientService,
    private socketService: SocketService
  ) {
    this.subscribeToCurrentPatient();
    this.setupSocketListeners();
  }

  private subscribeToCurrentPatient(): void {
    this.patientService.currentPatient$.pipe(
      switchMap(patient => {
        if (patient) {
          this.patientId = patient.id;
          this.patientCommunicationSubject.next([]);
          this.patientEmailsSubject.next([]);
          return forkJoin([this.loadCommunicationByPatient(patient.id), this.loadEmailsByPatient(patient.id)]);
        } else {
          this.patientCommunicationSubject.next([]);
          this.patientEmailsSubject.next([]);
          return []; // Return an empty array to satisfy the Observable return type
        }
      }),
      tap(([results, emails]: [any, any]) => {
        // Update the BehaviorSubject with the new or cleared notes
        const codes = results ? results.data : [];
        this.patientCommunicationSubject.next(codes);
        this.patientEmailsSubject.next(emails?.data || []);
        this.updatePatientMedia();
      })
    ).subscribe();
  }

  private updatePatientMedia() {
    const notes = this.patientCommunicationSubject.getValue();
    // Extract all media from the notes into a single array
    const mediaArray = notes?.reduce((acc, note) => {
      if (note.media && Array.isArray(note.media)) {
        return acc.concat(note.media);
      }
      return acc;
    }, []);
    this.patientMediaSubject.next(mediaArray);
  }

  private setupSocketListeners(): void {

    // Current patient's ID
    let currentPatientId: string | null = null;

    // Subscribe to the current patient changes to keep track of the current patient's ID
    this.patientService.currentPatient$.subscribe(patient => {
      currentPatientId = patient ? patient.id : null;
    });

    this.socketService.listen('communicationAdded', 'patient-communication-service').subscribe(item => {
      if (item.patientId === currentPatientId) {
        const current = this.patientCommunicationSubject.getValue() || [];
        this.patientCommunicationSubject.next([...current, item]);
        this.updatePatientMedia();
      }
    });

    // Listener for when communication is updated
    this.socketService.listen('communicationUpdated', 'patient-communication-service').subscribe(updatedCommunication => {
      if (updatedCommunication.patientId === currentPatientId) {
        const current = this.patientCommunicationSubject.getValue() || [];
        const updated = current.map(comm => {
          if (comm.id === updatedCommunication.id) {
            // When communicationUpdated is received, it means conversion completed
            // So we should use the new media as-is (which includes the new images and PDF without processingStatus)
            // Only preserve processingStatus if the new media still has it (shouldn't happen, but safety check)
            const existingMedia = comm.media || [];
            const updatedMedia = (updatedCommunication.media || []).map((newMedia: any) => {
              // If new media has processingStatus, keep it
              // Otherwise, check if existing media had it and new media doesn't - in that case, remove it
              if (newMedia.processingStatus) {
                return newMedia;
              }
              // If new media doesn't have processingStatus, don't preserve it from existing
              // This ensures processing status is cleared when conversion completes
              return newMedia;
            });
            return {
              ...updatedCommunication,
              media: updatedMedia
            };
          }
          return comm;
        });
        this.patientCommunicationSubject.next(updated);
        this.updatePatientMedia();
      }
    });

    // Listener for when communication PDF conversion starts
    this.socketService.listen('communicationPdfConversionStarted', 'patient-communication-service').subscribe((data: any) => {
      if (data.patientId === currentPatientId && data.mediaId) {
        const current = this.patientCommunicationSubject.getValue() || [];
        const commIndex = current.findIndex(comm => comm.id === data.communicationId);
        
        if (commIndex >= 0) {
          const mediaList = current[commIndex].media as any[] || [];
          const mediaIndex = mediaList.findIndex(m => m.id === data.mediaId);
          
          if (mediaIndex >= 0) {
            // Mark PDF as processing
            mediaList[mediaIndex] = {
              ...mediaList[mediaIndex],
              processingStatus: 'processing'
            };
            current[commIndex] = {
              ...current[commIndex],
              media: mediaList
            };
            this.patientCommunicationSubject.next([...current]);
          }
        }
      }
    });

    // Listener for when communication PDF conversion completes
    this.socketService.listen('communicationPdfConversionCompleted', 'patient-communication-service').subscribe((data: any) => {
      if (data.patientId === currentPatientId) {
        // The communicationUpdated event will handle the full update with new images
        // But we can also proactively remove processingStatus here as a safety measure
        const current = this.patientCommunicationSubject.getValue() || [];
        const commIndex = current.findIndex(comm => comm.id === data.itemId);
        
        if (commIndex >= 0) {
          const mediaList = current[commIndex].media as any[] || [];
          // Remove processing status from PDF media
          const updatedMedia = mediaList.map((media: any) => {
            if (media.processingStatus === 'processing' && media.mediaType === 'application/pdf') {
              const { processingStatus, ...mediaWithoutStatus } = media;
              return mediaWithoutStatus;
            }
            return media;
          });
          
          current[commIndex] = {
            ...current[commIndex],
            media: updatedMedia
          };
          this.patientCommunicationSubject.next([...current]);
          this.updatePatientMedia();
        }
      }
    });
  }

  loadCommunicationByPatient(patientId: string): Observable<any[]> {
    const token = localStorage.getItem('token');
    return this.http.get<any[]>(`${this.apiUrl}/patient/${patientId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  loadEmailsByPatient(patientId: string): Observable<any[]> {
    const token = localStorage.getItem('token');
    return this.http.get<any[]>(`${this.apiUrl}/email-transaction/patient/${patientId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  /**
   * Email a generated PDF to a recipient.
   *
   * The server renders the PDF from `content` and `images` — EXCEPT when `preRenderedPdf` is set,
   * in which case it attaches those bytes verbatim and renders nothing. See
   * `tasks/pdf-letterhead-placement.md` §3.3.
   */
  sendAIContentEmail(req: SendAIContentEmailRequest) {
    const token = localStorage.getItem('token');
    const { patientId, type, name, email, content, speciality, images, attachSignature,
      signatureUrl, signatoryName, embedMediaAsGrid, pdfDate, preRenderedPdf } = req;
    return this.http.post<any>(this.apiUrl, { patientId, type, name, email, content, speciality, images, attachSignature, signatureUrl, signatoryName, embedMediaAsGrid, pdfDate, preRenderedPdf }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }
}

/**
 * Was twelve positional parameters, with call sites reading
 * `…, [], undefined, undefined, undefined, undefined, pdfDate)`.
 *
 * `preRenderedPdf` would have been the THIRTEENTH, and the field it has to survive alongside is one
 * the server drops silently if it goes missing — a miscounted `undefined` there produces a
 * plausible-looking wrong PDF and no error anywhere.
 */
export interface SendAIContentEmailRequest {
  patientId: string;
  type: string;
  name: string;
  email: string;
  content: string;
  speciality: string;
  images?: any[];
  attachSignature?: boolean;
  signatureUrl?: string;
  signatoryName?: string;
  embedMediaAsGrid?: boolean;
  pdfDate?: string | Date;

  /**
   * A PDF the client has ALREADY drawn, as a `data:application/pdf;base64,…` URL.
   *
   * Set this only when the emailed copy must be byte-identical to what the download produces —
   * today that is the returned practice document, whose cover page the server has no way to
   * reproduce. When set, `content` and `images` no longer affect the attachment (they are still
   * stored on the communication record, and `content` is still the email BODY).
   */
  preRenderedPdf?: string;
}
